import {
  AccomodationType,
  BookingStatus,
  CarType,
  FlightType,
  Proving,
} from 'model'
import moment from 'moment'

function getTotalCost(
  units: { dateFrom?: Date; dateTo?: Date }[],
  costPerDay: number | null | undefined,
  addFirstDay: boolean = true
) {
  if (!costPerDay) {
    return 0
  }
  return (
    units.reduce((prev, unit) => {
      if (!unit.dateFrom || !unit.dateTo) {
        return prev
      }
      return (
        prev +
        moment(unit.dateTo).diff(moment(unit.dateFrom), 'days') +
        (addFirstDay ? 1 : 0)
      )
    }, 0) * costPerDay
  )
}

export function getEstimatedCost(proving: Proving) {
  if (!(proving.provingGround && proving.provingGround.fixedRates)) {
    return 0
  }

  const rates = proving.provingGround.fixedRates
  let cost = 0

  if (rates.useParticipantPerDay) {
    cost += getTotalCost(proving.participants || [], rates.participantPerDay)
  }
  if (rates.useVehiclePerDay) {
    cost += getTotalCost(proving.vehicles || [], rates.vehiclePerDay)
  }
  if (rates.useFeePerDay) {
    cost += getTotalCost([proving], rates.feePerDay)
  }
  if (rates.useServiceTeamPerDay) {
    cost += getTotalCost([proving], rates.serviceTeamPerDay)
  }
  if (rates.useWorkshopPerDay) {
    cost += getTotalCost(
      (proving.resourceBookings || []).filter(
        (r) => r.resource.category === 'workshop'
      ),
      rates.workshopPerDay
    )
  }
  if (rates.useColdcellPerDay) {
    cost += getTotalCost(
      (proving.resourceBookings || []).filter(
        (r) => r.resource.category === 'coldCell'
      ),
      rates.coldcellPerDay
    )
  }
  if (rates.useAllowancePerDay) {
    cost += getTotalCost(proving.participants || [], rates.allowancePerDay)
  }
  if (rates.useHotelPerNight) {
    cost += getTotalCost(
      (proving.participants || [])
        .filter(
          (p) =>
            p.accomodation &&
            p.accomodation.accomodationType !== AccomodationType.NoAccomodation
        )
        .map((p) => p.accomodation!),
      rates.hotelPerNight,
      false
    )
  }
  if (rates.useCompactRentalPerDay) {
    cost += getTotalCost(
      (proving.participants || [])
        .filter((p) => p.rentalCar && p.rentalCar.carType === CarType.Compact)
        .map((p) => p.rentalCar!),
      rates.compactRentalPerDay
    )
  }
  if (rates.useMidSizeRentalPerDay) {
    cost += getTotalCost(
      (proving.participants || [])
        .filter((p) => p.rentalCar && p.rentalCar.carType === CarType.MidSize)
        .map((p) => p.rentalCar!),
      rates.midSizeRentalPerDay
    )
  }
  if (rates.useFullSizeRentalPerDay) {
    cost += getTotalCost(
      (proving.participants || [])
        .filter((p) => p.rentalCar && p.rentalCar.carType === CarType.FullSize)
        .map((p) => p.rentalCar!),
      rates.fullSizeRentalPerDay
    )
  }
  if (rates.useEstateRentalPerDay) {
    cost += getTotalCost(
      (proving.participants || [])
        .filter((p) => p.rentalCar && p.rentalCar.carType === CarType.Estate)
        .map((p) => p.rentalCar!),
      rates.estateRentalPerDay
    )
  }
  if (rates.useMinibusRentalPerDay) {
    cost += getTotalCost(
      (proving.participants || [])
        .filter((p) => p.rentalCar && p.rentalCar.carType === CarType.Minibus)
        .map((p) => p.rentalCar!),
      rates.minibusRentalPerDay
    )
  }
  if (rates.useFlightPerRoute) {
    ;(proving.participants || []).forEach((p) => {
      if (p.flight) {
        switch (p.flight.flightType) {
          case FlightType.NoFlight:
            break
          case FlightType.OutboundFlight: {
            cost += rates.flightPerRoute || 0
            break
          }
          case FlightType.ReturnFlight: {
            cost += rates.flightPerRoute || 0
            break
          }
          case FlightType.Roundtrip: {
            cost += 2 * (rates.flightPerRoute || 0)
            break
          }
          default:
            break
        }
      }
    })
  }
  if (rates.useFerryPerParticipant) {
    cost +=
      (proving.participants || []).filter(
        (p) => p.ferry && p.ferry.bookingStatus !== BookingStatus.NotRequired
      ).length * (rates.ferryPerParticipant || 0)
  }
  if (rates.useFerryPerVehicle) {
    cost += (proving.vehicles || []).length * (rates.ferryPerVehicle || 0)
  }

  return parseFloat(cost.toFixed(2))
}
